/**
 * Users GraphQL Service
 * Consumes user-related GraphQL queries from resources/core
 */

import { graphqlClient } from '@/services/core/graphqlClient';
import { USERS_QUERIES } from '@/services/resources';

export interface PatientEnroller {
  patient_enroller_id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  clinic_names: string;
  clinic_ids: number[];
  deferred_task: number;
  follow_up: number;
}

export interface EnrollmentSpecialist {
  hbox_user_id: number;
  es_name: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  deferred_task?: number;
  clinic_ids?: number[];
  follow_up?: number;
}

export interface CareSpecialist {
  care_specialist_id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  clinic_names: string;
  clinic_ids: number[];
  deferred_task: number;
  follow_up: number;
}

export interface HBoxUser {
  id: number;
  first_name: string;
  last_name: string;
  type: string;
}

export interface ClinicAssignment {
  clinic_ids: number[];
}

export interface UserDashboard {
  id: number;
  user_name: string;
  email: string;
  user_type: string;
  phone_number: string;
  updated: string;
  last_active: string;
}

export interface UserGroupDashboard {
  user_group_id: number;
  user_group_name: string;
  user_count: number;
  user_ids?: number[];
  created_datetime: string;
  updated_datetime: string;
}

export interface UserRole {
  role: string;
  role_id: number;
}

export interface UserGroupAuditLog {
  pep_user_group_audit_log_id: number;
  user_group_id: number;
  action_type: string;
  action_datetime: string;
  action_by: string;
  user_data: any;
  user_group_data: any;
  notes: string;
  metadata: any;
}

export interface ResourceAvailabilityDayTime {
  available_end_time: string;
  available_start_time: string;
  day_of_week: number;
  resource_availability_id: number;
}

export interface ResourceAvailability {
  is_active: boolean;
  is_deleted: boolean;
  available_from_date: string;
  available_to_date: string;
  created_by: number;
  resource_availability_id: number;
  updated_by: number | null;
  user_id: number;
  user_timezone: string;
  created_datetime: string;
  updated_datetime: string | null;
  resourse_availbility: ResourceAvailabilityDayTime[];
}

export class UsersGraphqlService {
  /**
   * Get all patient enrollers
   */
  async getPatientEnrollers(hasuraToken: string) {
    return await graphqlClient.query<{ pep_vw_patient_enroller_list: PatientEnroller[] }>(
      USERS_QUERIES.GET_PATIENT_ENROLLERS,
      {},
      hasuraToken
    );
  }

  /**
   * Get enrollment specialists
   */
  async getEnrollmentSpecialists(hasuraToken: string) {
    return await graphqlClient.query<{ pep_vw_enrollment_specialists: EnrollmentSpecialist[] }>(
      USERS_QUERIES.GET_ENROLLMENT_SPECIALISTS,
      {},
      hasuraToken
    );
  }

  /**
   * Get enrollment specialists list (detailed)
   */
  async getEnrollmentSpecialistsList(hasuraToken: string) {
    return await graphqlClient.query<{ pep_vw_enrollment_specialists: EnrollmentSpecialist[] }>(
      USERS_QUERIES.GET_ENROLLMENT_SPECIALISTS_LIST,
      {},
      hasuraToken
    );
  }

  /**
   * Get care specialists
   */
  async getCareSpecialists(hasuraToken: string) {
    return await graphqlClient.query<{ pep_vw_care_specialist_list: CareSpecialist[] }>(
      USERS_QUERIES.GET_CARE_SPECIALISTS,
      {},
      hasuraToken
    );
  }

  /**
   * Get care specialist status
   */
  async getCareSpecialistStatus(hasuraToken: string, careSpecialistId: number) {
    return await graphqlClient.query<{ pep_vw_care_specialist_list: CareSpecialist[] }>(
      USERS_QUERIES.GET_CARE_SPECIALIST_STATUS,
      { careSpecialistId },
      hasuraToken
    );
  }

  /**
   * Get care specialist assignments
   */
  async getCareSpecialistAssignments(hasuraToken: string, careSpecialistId: number) {
    return await graphqlClient.query<{ pep_care_specialist_active_assignments: ClinicAssignment[] }>(
      USERS_QUERIES.GET_CARE_SPECIALIST_ASSIGNMENTS,
      { careSpecialistId },
      hasuraToken
    );
  }

  /**
   * Get patient enroller clinic IDs
   */
  async getPatientEnrollerClinicIds(hasuraToken: string, patientEnrollerId: number) {
    return await graphqlClient.query<{ pep_patient_enroller_active_assignments: ClinicAssignment[] }>(
      USERS_QUERIES.GET_PE_CLINIC_IDS,
      { patientEnrollerId },
      hasuraToken
    );
  }

  /**
   * Get patient enroller status
   */
  async getPatientEnrollerStatus(hasuraToken: string, patientEnrollerId: number) {
    return await graphqlClient.query<{ pep_vw_patient_enroller_list: PatientEnroller[] }>(
      USERS_QUERIES.GET_PE_STATUS,
      { patientEnrollerId },
      hasuraToken
    );
  }

  /**
   * Get HBox users
   */
  async getHBoxUsers(hasuraToken: string) {
    return await graphqlClient.query<{ api_hboxuser: HBoxUser[] }>(
      USERS_QUERIES.GET_HBOX_USERS,
      {},
      hasuraToken
    );
  }

  /**
   * Get HBox users by a list of IDs
   */
  async getHBoxUsersByIds(hasuraToken: string, ids: number[]) {
    return await graphqlClient.query<{ api_hboxuser: HBoxUser[] }>(
      USERS_QUERIES.GET_HBOX_USERS_BY_IDS,
      { ids },
      hasuraToken
    );
  }

  /**
   * Get VPEs (Virtual Patient Enrollers)
   */
  async getVPEs(hasuraToken: string) {
    return await graphqlClient.query<{ pep_vw_enrollment_specialists: EnrollmentSpecialist[] }>(
      USERS_QUERIES.GET_VPES,
      {},
      hasuraToken
    );
  }

  /**
   * Get VPE deferred patient list
   */
  async getVPEDeferredList(hasuraToken: string) {
    return await graphqlClient.query<{ pep_vw_vpe_deferred_list: Array<{ pep_patient_clinic_data_id: number }> }>(
      USERS_QUERIES.ES_DEFERRED_PATIENT_LIST,
      {},
      hasuraToken
    );
  }

  /**
   * Get ES deferred task permission
   */
  async getESDeferredTaskPermission(hasuraToken: string, userId: number) {
    return await graphqlClient.query<{ pep_vw_enrollment_specialists: Array<{ deferred_task: number; hbox_user_id: number }> }>(
      USERS_QUERIES.ES_DEFERRED_TASK_PERMISSION,
      { userId },
      hasuraToken
    );
  }

  /**
   * Get paginated users dashboard data
   */
  async getUsersDashboard(
    hasuraToken: string,
    limit: number,
    offset: number,
    orderBy?: Array<{ [key: string]: string }>
  ) {
    return await graphqlClient.query<{
      pep_vw_user_dashboard: UserDashboard[];
      pep_vw_user_dashboard_aggregate: { aggregate: { count: number } };
    }>(
      USERS_QUERIES.GET_USERS_DASHBOARD,
      {
        limit,
        offset,
        orderBy: orderBy || [{ id: 'asc' }]
      },
      hasuraToken
    );
  }

  /**
   * Search users dashboard with filters
   */
  async searchUsersDashboard(
    hasuraToken: string,
    limit: number,
    offset: number,
    where: Record<string, any>,
    orderBy?: Array<{ [key: string]: string }>
  ) {
    return await graphqlClient.query<{
      pep_vw_user_dashboard: UserDashboard[];
      pep_vw_user_dashboard_aggregate: { aggregate: { count: number } };
    }>(
      USERS_QUERIES.SEARCH_USERS_DASHBOARD,
      {
        limit,
        offset,
        where,
        orderBy: orderBy || [{ id: 'asc' }]
      },
      hasuraToken
    );
  }

  /**
   * Get single user by ID
   */
  async getUserById(hasuraToken: string, id: number) {
    return await graphqlClient.query<{
      pep_vw_user_dashboard: UserDashboard[];
    }>(
      USERS_QUERIES.GET_USER_BY_ID,
      { id },
      hasuraToken
    );
  }

  /**
   * Get a user's login type and Outlook email mapping (for pre-populating
   * the "Enable Outlook Sign-In" checkbox/field when editing a user)
   */
  async getHBoxUserLoginInfo(hasuraToken: string, id: number) {
    return await graphqlClient.query<{
      api_hboxuser_by_pk: { login_type: string | null; outlook_mapping: { identifier?: string, principal_email:string | null } | null } | null;
    }>(
      USERS_QUERIES.GET_HBOX_USER_LOGIN_INFO,
      { id },
      hasuraToken
    );
  }

  /**
   * Get multiple users by IDs
   */
  async getUsersByIds(hasuraToken: string, userIds: number[]) {
    return await graphqlClient.query<{
      pep_vw_user_dashboard: UserDashboard[];
    }>(
      USERS_QUERIES.GET_USERS_BY_IDS,
      { userIds },
      hasuraToken
    );
  }

  /**
   * Get paginated user groups dashboard data
   */
  async getUserGroupsDashboard(
    hasuraToken: string,
    limit: number,
    offset: number,
    orderBy?: Array<{ [key: string]: string }>
  ) {
    return await graphqlClient.query<{
      pep_vw_user_group_dashboard: UserGroupDashboard[];
      pep_vw_user_group_dashboard_aggregate: { aggregate: { count: number } };
    }>(
      USERS_QUERIES.GET_USER_GROUPS_DASHBOARD,
      {
        limit,
        offset,
        orderBy: orderBy || [{ user_group_id: 'asc' }]
      },
      hasuraToken
    );
  }

  /**
   * Search user groups dashboard with filters
   */
  async searchUserGroupsDashboard(
    hasuraToken: string,
    limit: number,
    offset: number,
    where: Record<string, any>,
    orderBy?: Array<{ [key: string]: string }>
  ) {
    return await graphqlClient.query<{
      pep_vw_user_group_dashboard: UserGroupDashboard[];
      pep_vw_user_group_dashboard_aggregate: { aggregate: { count: number } };
    }>(
      USERS_QUERIES.SEARCH_USER_GROUPS_DASHBOARD,
      {
        limit,
        offset,
        where,
        orderBy: orderBy || [{ user_group_id: 'asc' }]
      },
      hasuraToken
    );
  }

  /**
   * Get all user groups for dropdown
   */
  async getAllUserGroupsForDropdown(hasuraToken: string) {
    return await graphqlClient.query<{
      pep_vw_user_group_dashboard: UserGroupDashboard[];
    }>(
      USERS_QUERIES.GET_ALL_USER_GROUPS_FOR_DROPDOWN,
      {},
      hasuraToken
    );
  }

  /**
   * Get single user group by ID
   */
  async getUserGroupById(hasuraToken: string, userGroupId: number) {
    return await graphqlClient.query<{
      pep_vw_user_group_dashboard: UserGroupDashboard[];
    }>(
      USERS_QUERIES.GET_USER_GROUP_BY_ID,
      { userGroupId },
      hasuraToken
    );
  }

  /**
   * Get reference user roles for dropdown
   */
  async getReferenceUserRoles(hasuraToken: string) {
    return await graphqlClient.query<{
      reference_user_role: UserRole[];
    }>(
      USERS_QUERIES.GET_USER_ROLES,
      {},
      hasuraToken
    );
  }

  /**
   * Get user group audit log
   */
  async getUserGroupAuditLog(hasuraToken: string, userGroupId: number) {
    return await graphqlClient.query<{
      pep_user_group_audit_log: UserGroupAuditLog[];
      pep_user_group_audit_log_aggregate: { aggregate: { count: number } };
    }>(
      USERS_QUERIES.GET_USER_GROUP_AUDIT_LOG,
      { userGroupId },
      hasuraToken
    );
  }

  /**
   * Get user resource availability (schedule configuration)
   */
  async getResourceAvailability(hasuraToken: string, userId: number) {
    return await graphqlClient.query<{
      ref_resource_availability: ResourceAvailability[];
    }>(
      USERS_QUERIES.GET_REF_RESOURCE_AVAILABILITY,
      { userId },
      hasuraToken
    );
  }
}

// Export singleton instance
export const usersGraphqlService = new UsersGraphqlService();
export default usersGraphqlService;